import { addDays } from 'date-fns/addDays';
import { addMinutes } from 'date-fns/addMinutes';
import { differenceInCalendarDays } from 'date-fns/differenceInCalendarDays';
import { endOfDay } from 'date-fns/endOfDay';
import { isAfter } from 'date-fns/isAfter';
import { isBefore } from 'date-fns/isBefore';
import { startOfDay } from 'date-fns/startOfDay';
import { html, nothing, type TemplateResult } from 'lit';

import {
  type CalendarOccurrence,
  expandCalendarOccurrences,
} from '../calendar-domain.js';
import { dateAtDayMinute } from '../core/calendar-time-math.js';
import type { CalendarMode } from '../define.js';
import { calendarFeedbackMotion } from './calendar-chrome-motion.js';
import {
  type CalendarDisplaySettings,
  DEFAULT_CALENDAR_DISPLAY_SETTINGS,
} from './calendar-display-policy.js';
import {
  type CalendarSurfaceRange,
  calendarSurfaceWindow,
} from './calendar-surface-range.js';
import { styles } from './calendar-view-styles.js';
import type {
  CalendarDragOffset,
  CalendarEvent,
  CalendarEventEditPreview,
  CalendarRangeLoading,
} from './calendar-view-types.js';
import {
  CALENDAR_ALL_DAY_RENDER_PAGE_SIZE,
  type CalendarGridRenderContext,
  readCalendarPointerMoveTarget,
  renderCalendarGrid,
  renderCalendarOverlays,
} from './grids/calendar-grid-renderer.js';

export type {
  CalendarSurfaceRange,
  CalendarSurfaceRangeLoadSession,
} from './calendar-surface-range.js';
export {
  beginCalendarSurfaceRangeLoad,
  CALENDAR_SURFACE_HOUR_HEIGHT,
  CALENDAR_SURFACE_TIME_AXIS_WIDTH,
  CALENDAR_SURFACE_WEEK_DAY_MIN_WIDTH,
  CALENDAR_SURFACE_WEEK_LOAD_MORE_DAYS,
  calendarSurfaceDays,
  calendarSurfaceMonthAnchorKey,
  calendarSurfaceRangeLoadIntent,
  calendarSurfaceScrollCursor,
  calendarSurfaceWeekDayWidth,
  calendarSurfaceWindow,
  createCalendarSurfaceRange,
  DEFAULT_CALENDAR_TIMED_SCROLL_MINUTE,
  extendCalendarSurfaceRange,
  restoreCalendarSurfaceRangeScroll,
  shiftCalendarSurfaceCursor,
} from './calendar-surface-range.js';

export type CalendarSurfaceRenderOptions = {
  beforeGrid?: TemplateResult | typeof nothing;
  context: CalendarGridRenderContext;
  days: readonly Date[];
  showOverlays?: boolean;
};

export type CalendarProjectionGridInput = {
  allDayCollapsed?: boolean;
  calendarIds?: readonly string[];
  cursor: Date;
  displaySettings?: CalendarDisplaySettings;
  dragCreateSlot?: {
    allDay?: boolean;
    day: Date;
    endDay: Date;
    endMinute: number;
    startMinute: number;
  };
  draggedOccurrence?: CalendarOccurrence<CalendarEvent>;
  draggedOccurrenceOffset?: CalendarDragOffset;
  eventEditPreview?: CalendarEventEditPreview;
  events: readonly CalendarEvent[];
  finishSlotDrag?: (day: Date, endMinute: number) => void;
  mode: CalendarMode;
  now?: Date;
  onClearDragCreateSlot?: () => void;
  onCreateEvent?: (day: Date) => void;
  onDragCreateSlot?: (day: Date, startMinute: number) => void;
  onDraggedOccurrenceChange?: (
    occurrence: CalendarOccurrence<CalendarEvent> | undefined,
    offset?: CalendarDragOffset
  ) => void;
  onEventEditCommit?: (
    event: CalendarEvent,
    startAt: Date,
    endAt: Date,
    options?: { allDay?: boolean }
  ) => void;
  onEventEditPreview?: (
    event: CalendarEvent,
    startAt: Date,
    endAt: Date,
    options?: { allDay?: boolean }
  ) => void;
  onClearEventEditPreview?: () => void;
  onEventRename?: (event: CalendarEvent, title: string) => void;
  onOpenDay: (day: Date) => void;
  onOpenWeek?: (day: Date) => void;
  onPreviewDragCreateSlot?: (day: Date, endMinute: number) => void;
  onSelectEvent: (event: CalendarEvent, occurrenceStart?: Date) => void;
  onToggleAllDayCollapsed?: () => void;
  rangeLoading?: CalendarRangeLoading;
  range?: CalendarSurfaceRange;
  readonly?: boolean;
  selectedOccurrenceStart?: string;
  selectedRowId?: string;
};

export function renderCalendarSurface({
  beforeGrid = nothing,
  context,
  days,
  showOverlays = true,
}: CalendarSurfaceRenderOptions): TemplateResult {
  return html`<main
    ${calendarFeedbackMotion()}
    class="${styles.calendar}"
    data-calendar-surface="true"
  >
    ${beforeGrid} ${showOverlays ? renderCalendarOverlays(context) : nothing}
    ${renderCalendarGrid(context, days)}
  </main>`;
}

/** Projection adapter for workspace-wide event aggregation. */
export function createCalendarProjectionGridContext({
  allDayCollapsed = false,
  calendarIds,
  cursor,
  displaySettings = DEFAULT_CALENDAR_DISPLAY_SETTINGS,
  dragCreateSlot,
  draggedOccurrence,
  draggedOccurrenceOffset,
  eventEditPreview,
  events,
  finishSlotDrag = noop,
  mode,
  now = new Date(),
  onClearDragCreateSlot = noop,
  onCreateEvent = noop,
  onDragCreateSlot = noop,
  onDraggedOccurrenceChange = noop,
  onEventEditCommit = noop,
  onEventEditPreview = noop,
  onClearEventEditPreview = noop,
  onEventRename = noop,
  onOpenDay,
  onOpenWeek = noop,
  onPreviewDragCreateSlot = noop,
  onSelectEvent,
  onToggleAllDayCollapsed = noop,
  rangeLoading,
  range,
  readonly = true,
  selectedOccurrenceStart,
  selectedRowId,
}: CalendarProjectionGridInput): CalendarGridRenderContext {
  const window = calendarSurfaceWindow(cursor, mode, range);
  const occurrences = events
    .flatMap(event =>
      expandCalendarOccurrences(
        event,
        new Date(window.startAt),
        new Date(window.endAt)
      )
    )
    .sort((left, right) => left.startAt.getTime() - right.startAt.getTime());
  const resolvedCalendarIds =
    calendarIds ??
    [...new Set(events.map(event => event.calendarId).filter(Boolean))].sort(
      (left, right) => left.localeCompare(right)
    );

  return {
    allDayCollapsed,
    allDayRenderLimit: CALENDAR_ALL_DAY_RENDER_PAGE_SIZE,
    allDayEventsForDays: days =>
      occurrences.filter(
        occurrence =>
          occurrence.source.allDay &&
          overlapsDays(occurrence, days[0], days.at(-1))
      ),
    calendarIds: resolvedCalendarIds,
    cursor,
    displaySettings,
    draggedOccurrence,
    draggedOccurrenceOffset,
    dragCreateSlot,
    eventEditPreview,
    eventsForDay: day =>
      occurrences.filter(occurrence => overlapsDay(occurrence, day)),
    finishSlotDrag,
    mode,
    now,
    onClearDragCreateSlot,
    onClearOccurrencePreview: onClearEventEditPreview,
    onCommitOccurrenceMove: (event, day, startMinute) => {
      const next = resolveProjectionTimedMove(event, day, startMinute);
      onEventEditCommit(event.source, next.startAt, next.endAt, {
        allDay: false,
      });
    },
    onCommitOccurrenceMoveToAllDay: (event, day) => {
      const next = resolveProjectionAllDayMove(event, day);
      onEventEditCommit(event.source, next.startAt, next.endAt, {
        allDay: true,
      });
    },
    onCommitOccurrenceMoveToDay: (event, day) => {
      const next = resolveProjectionDayMove(event, day);
      onEventEditCommit(event.source, next.startAt, next.endAt, {
        allDay: event.source.allDay,
      });
    },
    onCommitOccurrenceResize: (event, startAt, endAt) =>
      onEventEditCommit(event.source, startAt, endAt, {
        allDay: event.source.allDay,
      }),
    onCreateEvent: day => onCreateEvent(day ?? cursor),
    onDragCreateSlot,
    onDraggedOccurrenceChange,
    onEventContextMenu: noop,
    onEventKeyDown: (key, occurrence) => {
      if (key.key !== 'Enter' && key.key !== ' ') return;
      key.preventDefault();
      onSelectEvent(occurrence.source, occurrence.startAt);
    },
    onJumpToHiddenTimedEvents: noop,
    onMoveOccurrenceToDay: noop,
    onOpenDay,
    onOpenWeek,
    onPreviewDragCreateSlot,
    onPreviewOccurrenceMove: (event, day, startMinute) => {
      const next = resolveProjectionTimedMove(event, day, startMinute);
      onEventEditPreview(event.source, next.startAt, next.endAt, {
        allDay: false,
      });
    },
    onPreviewOccurrenceMoveToAllDay: (event, day) => {
      const next = resolveProjectionAllDayMove(event, day);
      onEventEditPreview(event.source, next.startAt, next.endAt, {
        allDay: true,
      });
    },
    onPreviewOccurrenceMoveToDay: (event, day) => {
      const next = resolveProjectionDayMove(event, day);
      onEventEditPreview(event.source, next.startAt, next.endAt, {
        allDay: event.source.allDay,
      });
    },
    onPreviewOccurrenceResize: (event, startAt, endAt) =>
      onEventEditPreview(event.source, startAt, endAt, {
        allDay: event.source.allDay,
      }),
    onRefreshIcsSubscription: noop,
    onRemoveIcsSubscription: noop,
    onRenameOccurrence: (occurrence, title) => {
      if (readonly || occurrence.source.externalReadonly) return;
      onEventRename(occurrence.source, title);
    },
    onSelectOccurrence: occurrence =>
      onSelectEvent(occurrence.source, occurrence.startAt),
    onShowMoreAllDayEvents: noop,
    onSetIcsSubscriptionEnabled: noop,
    onSetVisibleCalendarIds: noop,
    onStartResizePointer: (pointer, event, edge) =>
      startProjectionTimedResizePointer(
        pointer,
        event,
        edge,
        onEventEditPreview,
        onEventEditCommit,
        onClearEventEditPreview
      ),
    onToggleAllDayCollapsed,
    rangeLoading,
    readonly,
    recurrenceErrors: new Map(),
    selectedOccurrenceStart,
    selectedRowId,
    subscriptionStates: {},
    subscriptions: [],
    timedOffscreen: { bottom: 0, top: 0 },
    timedRenderWindow: { endMinute: 24 * 60, startMinute: 0 },
    visibleCalendarIds: [],
  };
}

function overlapsDay(
  occurrence: CalendarOccurrence<CalendarEvent>,
  day: Date
): boolean {
  return !(
    isAfter(occurrence.startAt, endOfDay(day)) ||
    isBefore(occurrence.endAt, startOfDay(day))
  );
}

function overlapsDays(
  occurrence: CalendarOccurrence<CalendarEvent>,
  first: Date | undefined,
  last: Date | undefined
): boolean {
  if (!first || !last) return false;
  return !(
    isAfter(occurrence.startAt, endOfDay(last)) ||
    isBefore(occurrence.endAt, startOfDay(first))
  );
}

function resolveProjectionTimedMove(
  event: CalendarOccurrence<CalendarEvent>,
  day: Date,
  startMinute: number
): { endAt: Date; startAt: Date } {
  const durationMinutes = Math.max(
    15,
    Math.round((event.endAt.getTime() - event.startAt.getTime()) / 60_000)
  );
  const startAt = dateAtDayMinute(day, Math.round(startMinute / 15) * 15);
  return { endAt: addMinutes(startAt, durationMinutes), startAt };
}

function resolveProjectionDayMove(
  event: CalendarOccurrence<CalendarEvent>,
  day: Date
): { endAt: Date; startAt: Date } {
  const delta = differenceInCalendarDays(day, event.startAt);
  return {
    endAt: addDays(event.endAt, delta),
    startAt: addDays(event.startAt, delta),
  };
}

function resolveProjectionAllDayMove(
  event: CalendarOccurrence<CalendarEvent>,
  day: Date
): { endAt: Date; startAt: Date } {
  const daySpan = Math.max(
    1,
    differenceInCalendarDays(
      startOfDay(event.endAt),
      startOfDay(event.startAt)
    ) + 1
  );
  const startAt = startOfDay(day);
  return { endAt: endOfDay(addDays(startAt, daySpan - 1)), startAt };
}

export function resolveProjectionTimedResize(
  initialStart: Date,
  initialEnd: Date,
  edge: 'end' | 'start',
  targetDay: Date,
  targetMinute: number
): { endAt: Date; startAt: Date } | undefined {
  const target = addMinutes(startOfDay(targetDay), targetMinute);
  if (edge === 'start') {
    return isBefore(target, addMinutes(initialEnd, -15))
      ? { endAt: initialEnd, startAt: target }
      : undefined;
  }
  return isAfter(target, addMinutes(initialStart, 15))
    ? { endAt: target, startAt: initialStart }
    : undefined;
}

function startProjectionTimedResizePointer(
  pointer: PointerEvent,
  event: CalendarOccurrence<CalendarEvent>,
  edge: 'end' | 'start',
  preview: NonNullable<CalendarProjectionGridInput['onEventEditPreview']>,
  commit: NonNullable<CalendarProjectionGridInput['onEventEditCommit']>,
  clear: NonNullable<CalendarProjectionGridInput['onClearEventEditPreview']>
): void {
  pointer.preventDefault();
  pointer.stopPropagation();
  const target = pointer.currentTarget as HTMLElement;
  target.setPointerCapture?.(pointer.pointerId);
  const initialY = pointer.clientY;
  const initialStart = event.source.startAt;
  const initialEnd = event.source.endAt;
  let latestStart = initialStart;
  let latestEnd = initialEnd;

  const onMove = (move: PointerEvent) => {
    const pointerTarget = readCalendarPointerMoveTarget(move);
    const absoluteResize =
      pointerTarget?.kind === 'time'
        ? resolveProjectionTimedResize(
            initialStart,
            initialEnd,
            edge,
            pointerTarget.day,
            pointerTarget.startMinute
          )
        : undefined;
    if (pointerTarget?.kind === 'time' && !absoluteResize) return;
    const minutes = Math.round((move.clientY - initialY) / 12) * 15;
    if (edge === 'start') {
      const nextStart =
        absoluteResize?.startAt ?? addMinutes(initialStart, minutes);
      if (!isBefore(nextStart, addMinutes(initialEnd, -15))) return;
      latestStart = nextStart;
      latestEnd = initialEnd;
    } else {
      const nextEnd = absoluteResize?.endAt ?? addMinutes(initialEnd, minutes);
      if (!isAfter(nextEnd, addMinutes(initialStart, 15))) return;
      latestStart = initialStart;
      latestEnd = nextEnd;
    }
    preview(event.source, latestStart, latestEnd);
  };
  const cleanup = () => {
    target.removeEventListener('pointermove', onMove);
    target.removeEventListener('pointerup', onUp);
    target.removeEventListener('pointercancel', onCancel);
    if (target.hasPointerCapture?.(pointer.pointerId)) {
      target.releasePointerCapture?.(pointer.pointerId);
    }
  };
  const onUp = () => {
    cleanup();
    if (
      latestStart.getTime() === initialStart.getTime() &&
      latestEnd.getTime() === initialEnd.getTime()
    ) {
      clear();
      return;
    }
    commit(event.source, latestStart, latestEnd);
  };
  const onCancel = () => {
    cleanup();
    clear();
  };
  target.addEventListener('pointermove', onMove);
  target.addEventListener('pointerup', onUp);
  target.addEventListener('pointercancel', onCancel);
}

function noop(..._args: unknown[]): void {}
